import { ActionIcon } from '@any-ui-react/core';
import { useMemo } from 'react';

import { useDeleteRecordModal } from '@/common/components/shared/delete-record-modal/useDeleteRecordModal';
import { MaterialIcons } from '@/common/components/shared/material-icons/MaterialIcons';
import { NoResult } from '@/common/components/shared/no-result/NoResult';
import { getPhoneNumberText } from '@/common/components/shared/phone-number-text/PhoneNumberText';
import { Table, type TableLayout } from '@/common/components/shared/table/Table';
import { useEmployeeDetailContext } from '@/common/contexts/EmployeeDetailContext';
import { useEmployeeAccess } from '@/common/hooks/employees/useEmployeeAccess';
import { EmergencyContactRelationLabels, type EmergencyContactType } from '@/common/types';

import { EmergencyContactModal } from './EmergencyContactModal';

interface EmergencyContactTableProps {
  loading: boolean;
  items: EmergencyContactType[];
  onRemove: (id: string) => void;
  onUpdate: (data: EmergencyContactType) => void;
  isEditable?: boolean;
}

export const EmergencyContactTable = ({
  items,
  loading,
  onRemove,
  onUpdate,
  isEditable,
}: EmergencyContactTableProps) => {
  const { openDeleteRecordModal } = useDeleteRecordModal();

  const { employeeDetail } = useEmployeeDetailContext();
  const isNewRecord = !employeeDetail.id;
  const { hasEmployeePersonalInformationDeleteAccess } = useEmployeeAccess();
  const isRemovable = hasEmployeePersonalInformationDeleteAccess || isNewRecord;

  const layout: Array<TableLayout<EmergencyContactType>> = useMemo(
    () => [
      {
        headerRender: () => 'Name',
        accessor: 'name',
        cellRender: emergencyContact =>
          isEditable ? (
            <EmergencyContactModal className="ml-auto" emergencyContact={emergencyContact} onSuccess={onUpdate}>
              <span className="text-blue-6 cursor-pointer">{emergencyContact.name}</span>
            </EmergencyContactModal>
          ) : (
            emergencyContact.name
          ),
      },

      {
        headerRender: () => 'Relation',
        accessor: 'relation',
        cellRender: ({ relation }) => EmergencyContactRelationLabels[relation],
      },
      {
        headerRender: () => 'Phone Number',
        accessor: 'phoneNumber',
        cellRender: ({ phoneNumber, phoneNumberCountryCode, phoneNumberCountryIsdCode }) =>
          getPhoneNumberText(phoneNumber, phoneNumberCountryCode, phoneNumberCountryIsdCode),
      },
      {
        headerRender: () => 'Remarks',
        accessor: 'remarks',
        cellRender: ({ remarks }) => remarks && <div className="whitespace-pre-wrap">{remarks}</div>,
      },
      ...(isRemovable
        ? [
            {
              headerRender: () => '',
              accessor: 'delete',
              thClassName: '!max-w-[72px]',
              tdClassName: '!max-w-[72px]',
              cellRender: ({ id }: EmergencyContactType) => (
                <ActionIcon
                  className="ml-auto"
                  size="xs"
                  onClick={() => {
                    openDeleteRecordModal({
                      onSuccess: () => onRemove(id),
                    });
                  }}
                >
                  <MaterialIcons iconName="delete" className="cursor-pointer" />
                </ActionIcon>
              ),
            },
          ]
        : []),
    ],
    [isEditable, isRemovable, onRemove, onUpdate, openDeleteRecordModal],
  );

  return (
    <Table<EmergencyContactType>
      data={items || []}
      emptyContainerClassName="border-gray-2 text-center"
      headerClassName="[&_th]:border-t-0"
      layout={layout}
      loading={loading}
      notFoundTemplate={
        <NoResult title="No emergency contacts found" titleClassName="text-sm text-gray-9" wrapperClassName="py-10" />
      }
      numOfStickyCols={0}
      wrapperClassName="border"
    />
  );
};
